import { RefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  continueProcessing,
  requestSdkProcessor,
  selectPaymentFormPayload,
  setPaymentFormStatus,
  setPaymentResult,
  updatePaymentFormPayloadItem,
} from '@cashier/cashier-data-access';
import {
  CashierMessages,
  FinalStates,
  FormStatuses,
  LeaderIframeMessages,
  ResultMessageTypes,
  SecuredFieldPayload,
} from '@cashier/cashier-model';
import { eventBus, EventFactory } from '@cashier/cashier-shared';

import { useSecuredFormStateContext } from '../../../../../contexts';

interface UseLeaderIframeMessagesParams {
  iframeRef: RefObject<HTMLIFrameElement | null>;
}

export const useLeaderIframeMessages = ({
  iframeRef,
}: UseLeaderIframeMessagesParams): void => {
  const dispatch = useDispatch();
  const payload = useSelector(selectPaymentFormPayload);
  const { setPayloadItem } = useSecuredFormStateContext();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const iframeWindow = iframeRef?.current?.contentWindow;
      if (!iframeWindow || event.source !== iframeWindow) return;

      const { data } = event;

      if (data?.type === CashierMessages.CashierData) {
        eventBus.emit(
          EventFactory.cashierMessage({
            task: CashierMessages.CashierData,
          }),
        );
      }

      if (data?.type === LeaderIframeMessages.TransferDataToParent) {
        const {
          name,
          payload,
        }: { name: string; payload: SecuredFieldPayload } = data.payload;

        const { value, validationStatus, errors, isFocused, isFilled } =
          payload;
        setPayloadItem(name, { isFilled, isFocused });

        dispatch(
          updatePaymentFormPayloadItem({
            name,
            fieldPayload: {
              value,
              validationStatus,
              interactionCompleted: true,
              errors,
            },
          }),
        );
      }

      if (
        data?.type === LeaderIframeMessages.PayloadEncrypted ||
        data?.type === LeaderIframeMessages.TokenizerV1
      ) {
        dispatch(continueProcessing(data.payload));
      }

      if (data?.type === LeaderIframeMessages.SdkProcessorPaymentProcessed) {
        dispatch(requestSdkProcessor(data.payload));
      }

      if (data?.type === LeaderIframeMessages.PreprocessorLoaded) {
        const { success, message } = data.payload;

        if (success) {
          dispatch(setPaymentFormStatus(FormStatuses.READY));
        } else {
          dispatch(
            setPaymentResult({
              finalState: FinalStates.PAYMENT_METHOD_ERROR,
              message: {
                content: message,
                type: ResultMessageTypes.STRING,
              },
            }),
          );
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [iframeRef, dispatch, payload, setPayloadItem]);
};
